import moment from "moment";

import formatComma from "react-lib/utils/formatComma";
import { formatDate, formatDatetime } from "react-lib/utils/dateUtils";

/** แพ็กเกจที่เปิดขาย (core/package/) */
export type PackageItem = {
  id: number;
  code: string;
  name: string;
  name_en?: string;
  package_type?: number | null;
  package_service_type?: number | null;
  gender?: string | null;
  age_limits_year_start?: number | null;
  age_limits_year_end?: number | null;
  price?: string | number | null;
  lifespan_year?: number | null;
  lifespan_month?: number | null;
  lifespan_day?: number | null;
  start_date?: string;
  end_date?: string | null;
};

/** รายการในแพ็กเกจ (core/package/<id>/ items) */
export type PackageDetailItem = {
  id: number;
  product_code?: string;
  product_name?: string;
  product_name_en?: string;
  p_type_code?: string;
  package_group_code?: string;
  unit_name?: string;
  quantity?: string | number;
  is_display?: boolean;
  active?: boolean;
};

export type PackageDetail = PackageItem & { items?: PackageDetailItem[] };

/** รายการในแพ็กเกจที่ซื้อ (core/package-order-with-item/ items) */
export type PackageOrderItem = {
  id: number;
  product?: number;
  product_code?: string;
  product_name?: string;
  product_name_en?: string;
  product_package_group_code?: string;
  quantity?: string | number;
  quantity_left?: string | number;
  active?: boolean;
};

/** แพ็กเกจที่ผู้ป่วยเลือก / ซื้อ (core/package-order-with-item/) */
export type PackageOrder = {
  id: number;
  product: number;
  product_code?: string;
  product_name?: string;
  product_name_en?: string;
  status?: "BILLED" | "PAID" | "CANCELED" | string;
  custom_status?: string;
  usage_status?: string;
  created_at?: string;
  paid_at?: string | null;
  appointment_datetime?: string | null;
  quantity?: string | number;
  price?: string | number;
  active?: boolean;
  items?: PackageOrderItem[];
};

/** ประวัติการใช้สิทธิ์ (APP/package-usage-history/) */
export type PackageUsageLog = {
  dt: string;
  usage_datetime?: string;
  status?: string;
  status_label?: string;
  quantity?: string | number;
  product_name?: string;
  package_order_id?: number;
  package_order_item_id?: number;
};

export type PackageTabKey = "sale" | "selected" | "history";

export type HistoryFilter = "all" | "left" | "done";

export type MasterOption = { key?: number | string; value: number | string; text: string };

export const PACKAGE_PAGE_SIZE = 10;

const GENDERS: Record<string, { icon: string; text: string }> = {
  FEMALE: { icon: "♀", text: "หญิง" },
  F: { icon: "♀", text: "หญิง" },
  MALE: { icon: "♂", text: "ชาย" },
  M: { icon: "♂", text: "ชาย" },
};

const toNumber = (value: unknown): number => Number(value) || 0;

export const genderOf = (gender?: string | null) =>
  GENDERS[String(gender || "").toUpperCase()] ?? { icon: "⚥", text: "ไม่ระบุเพศ" };

export const ageRangeText = (item: PackageItem): string =>
  `${item.age_limits_year_start || 1} - ${item.age_limits_year_end || 99}`;

export const priceText = (value: unknown): string => formatComma(toNumber(value), "en-US", false);

export const amountText = (value: unknown): string => formatComma(toNumber(value), "en-US", true);

export const lifespanText = (item: PackageItem): string => {
  const parts = [
    item.lifespan_year ? `${item.lifespan_year} ปี` : "",
    item.lifespan_month ? `${item.lifespan_month} เดือน` : "",
    item.lifespan_day ? `${item.lifespan_day} วัน` : "",
  ].filter(Boolean);

  return parts.join(" ") || "-";
};

export const optionText = (options: MasterOption[] | undefined, value: unknown): string =>
  options?.find((option) => String(option.value) === String(value))?.text ?? "";

/** มีแถวที่ยังไม่ชำระของแพ็กเกจนี้อยู่แล้ว */
export const unpaidOrderOf = (orders: PackageOrder[], packageId: number) =>
  orders.find((order) => order.product === packageId && order.status === "BILLED");

export const isPaid = (order: PackageOrder) => order.status === "PAID";

/** สิทธิ์ทั้งหมด / คงเหลือ / ใช้แล้ว ของแพ็กเกจที่ซื้อ */
export const usageOf = (order: PackageOrder) => {
  const items = (order.items ?? []).filter((item) => item.active !== false);
  let total = 0;
  let left = 0;
  for (const item of items) {
    total += toNumber(item.quantity);
    left += toNumber(item.quantity_left);
  }
  return { total, left, used: total - left };
};

export const usageText = (order: PackageOrder): string => {
  if (!isPaid(order)) return "-";
  const { total, used } = usageOf(order);
  return total > 0 && used >= total ? "ใช้ครบแล้ว" : `${used}/${total}`;
};

/** ข้อความเมื่อลบไม่ได้, null = ลบได้ */
export const deleteBlockText = (order: PackageOrder): string | null => {
  if (usageOf(order).used > 0) return "ลบไม่ได้ · มีการใช้สิทธิ์แพ็กเกจแล้ว";
  if (isPaid(order)) return "ลบไม่ได้ · ชำระเงินแล้ว";
  return null;
};

export const appointmentText = (order: PackageOrder): string =>
  order.appointment_datetime ? formatDatetime(order.appointment_datetime) : "-";

export const selectedDateText = (order: PackageOrder): string =>
  order.created_at ? formatDate(moment(order.created_at)) : "";

export const totalOf = (orders: PackageOrder[]): number => {
  let total = 0;
  for (const order of orders) total += toNumber(order.price);
  return total;
};

/** จัดกลุ่มรายการตามหมวด (LAB, TREATMENT, ...) ตามลำดับที่พบ */
export const groupByCategory = <T>(items: T[], categoryOf: (item: T) => string) => {
  const groups: { category: string; items: T[] }[] = [];
  for (const item of items) {
    const category = categoryOf(item) || "-";
    const group = groups.find((row) => row.category === category);
    if (group) {
      group.items.push(item);
    } else {
      groups.push({ category, items: [item] });
    }
  }
  return groups;
};

export const detailGroups = (detail: PackageDetail | null | undefined) =>
  groupByCategory(
    (detail?.items ?? []).filter((item) => item.active !== false && item.is_display !== false),
    (item) => item.package_group_code || item.p_type_code || "",
  );

/* ---------------- แท็บ 3 ประวัติและยอดคงเหลือ ---------------- */

export const isUsedUp = (order: PackageOrder): boolean => {
  const { total, left } = usageOf(order);
  return total > 0 && left <= 0;
};

export const historyOrders = (orders: PackageOrder[], filter: HistoryFilter): PackageOrder[] => {
  const paid = orders.filter(isPaid);
  if (filter === "left") return paid.filter((order) => !isUsedUp(order));
  if (filter === "done") return paid.filter(isUsedUp);
  return paid;
};

export const historySummary = (order: PackageOrder): string => {
  const { used, total, left } = usageOf(order);
  return `ใช้แล้ว ${used}/${total} ครั้ง · คงเหลือ ${left} ครั้ง`;
};

export const paidLineText = (order: PackageOrder): string =>
  `ชำระ ${order.paid_at ? formatDate(moment(order.paid_at)) : "-"} · ${priceText(order.price)} บาท`;

export const orderItemGroups = (order: PackageOrder) =>
  groupByCategory(
    (order.items ?? []).filter((item) => item.active !== false),
    (item) => item.product_package_group_code || "",
  );

/** ประวัติการใช้ของรายการหนึ่ง ใหม่สุดก่อน */
export const usageLogsOf = (logs: PackageUsageLog[], itemId: number) =>
  logs
    .filter((log) => log.package_order_item_id === itemId)
    .sort((a, b) => moment(b.dt).valueOf() - moment(a.dt).valueOf())
    .map((log) => ({
      at: formatDatetime(log.dt),
      text: [log.status_label, log.product_name].filter(Boolean).join(" "),
    }));

/** แพ็กเกจที่เทียบความคุ้มได้: รอชำระ ไม่อยู่ในตะกร้า/รอตรวจสลิป และยังไม่ใช้สิทธิ์ */
export const isComparable = (order: PackageOrder): boolean =>
  order.status === "BILLED" &&
  !["WAIT_CONFIRM_PAYMENT", "IN_CART"].includes(order.custom_status || "") &&
  usageOf(order).used === 0;

/** error ของ core/package/optimize/ (input_packages ตามลำดับที่ส่ง) เป็นข้อความต่อรหัสแพ็กเกจ */
export const optimizeErrorOf = (error: unknown, orders: PackageOrder[]): unknown => {
  const rows = (error as { input_packages?: unknown } | null)?.input_packages;
  if (!Array.isArray(rows)) return error;

  const texts: string[] = [];
  for (const [index, row] of rows.entries()) {
    const messages = Object.values((row ?? {}) as Record<string, unknown>)
      .flat()
      .map(String);
    if (messages.length) {
      texts.push(`${orders[index]?.product_code ?? index + 1}: ${messages.join(", ")}`);
    }
  }

  return texts.length ? { detail: texts.join(" · ") } : error;
};

export const quantityText = (value: unknown): string => String(toNumber(value));

/** รายการที่ยังมีสิทธิ์คงเหลือของแพ็กเกจที่ชำระแล้ว */
const orderableItemsOf = (orders: PackageOrder[]): PackageOrderItem[] =>
  orders
    .filter(isPaid)
    .flatMap((order) =>
      (order.items ?? []).filter(
        (item) => item.active !== false && toNumber(item.quantity_left) > 0,
      ),
    );

/** รายการคงเหลือของแพ็กเกจที่ชำระแล้ว ส่งเป็นคำสั่งจริงรายการละ 1 */
export const binderItemsOf = (orders: PackageOrder[]) =>
  orderableItemsOf(orders).map((item) => ({
    package_order_item: item.id,
    quantity_appointment: 1,
  }));

/** error ของ binder-to-real-order ("package_order_item (items[i])") เป็นข้อความต่อชื่อรายการ */
export const binderErrorOf = (error: unknown, orders: PackageOrder[]): unknown => {
  if (!error || typeof error !== "object") return error;

  const items = orderableItemsOf(orders);
  const texts: string[] = [];
  for (const [key, value] of Object.entries(error as Record<string, unknown>)) {
    const index = /items\[(\d+)\]/.exec(key)?.[1];
    if (index === undefined) return error;
    const name = items[Number(index)]?.product_name ?? key;
    texts.push(`${name}: ${[value].flat().map(String).join(", ")}`);
  }

  return texts.length ? { detail: texts.join(" · ") } : error;
};

/** แพ็กเกจที่ชำระแล้วและยังมีสิทธิ์คงเหลือ (ส่งเข้า Order ได้) */
export const orderableOf = (orders: PackageOrder[]): PackageOrder[] =>
  orders.filter((order) => orderableItemsOf([order]).length > 0);
